import {
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    inject,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    output,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogInstanceToken } from '@evorait/pace-logging';
import { debounceTime, delay, from, map, Observable, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { prepareZXingModule, readBarcodes, ReaderOptions, ReadResult } from 'zxing-wasm/full';
import {
    BARCODE_REFOCUS_DEBOUNCE,
    BARCODE_LOOP_DELAY,
    BARCODE_STREAM_RESTART_COOLDOWN,
    BARCODE_STREAM_RESTART_DELAY,
    BARCODE_DEFAULT_READER_OPTIONS,
} from './pace-barcode-scanner.const';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faRedo, faFilter } from '@fortawesome/free-solid-svg-icons';
import { RecognisedBarcode } from './pace-barcode-scanner.model';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ProgressbarComponent } from 'ngx-bootstrap/progressbar';
import { ApplicationIdentifier } from '../utils/scan-utilities/scan.enum';
import { ScanUtilities } from '../utils/scan-utilities/scan-utilities.service';
import { matchesPattern } from '../utils/scan-utilities/scan.utils';

prepareZXingModule({
    overrides: {
        locateFile: (path: string, prefix: string) => {
            if (path.endsWith('.wasm')) {
                return `${path}`;
            }
            return prefix + path;
        },
    },
});

@Component({
    selector: 'lib-pace-barcode-scanner',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, TooltipModule, ProgressbarComponent],
    templateUrl: './pace-barcode-scanner.component.html',
    styleUrl: './pace-barcode-scanner.component.css',
})
export class PaceBarcodeScannerComponent implements OnInit, OnDestroy {
    private readonly LOCAL_STORAGE_PREFIX = 'pace-barcode-scanner';
    private readonly LOCAL_STORAGE_LAST_CAM = `${this.LOCAL_STORAGE_PREFIX}_last-cam`;

    private readonly log = inject(LogInstanceToken);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly zone = inject(NgZone);
    private readonly scanUtilities = inject(ScanUtilities);

    public readonly pattern = input<string>();
    public readonly appId = input<ApplicationIdentifier>(ApplicationIdentifier.Default);
    public readonly rawHeight = input<number>(2000);
    public readonly readerOptions = input<ReaderOptions>(BARCODE_DEFAULT_READER_OPTIONS);
    public readonly mismatchTooltip = input<string>('Barcode does not match required pattern.');
    public readonly restartStreamLabel = input<string>('Restart Stream');
    public readonly togglePatternLabel = input<string>('Barcodes must match a pattern. Click to reveal the pattern.');
    public readonly pickLabel = input<string>('Pick Barcode');
    public readonly chooseWebcamLabel = input<string>('Choose Webcam');

    public readonly barcodeSelected = output<string>();
    public readonly barcodeSuccessfullyScanned = output<void>();

    @ViewChild('barcodeVideo') public videoElement: ElementRef | undefined;

    public devices: MediaDeviceInfo[] = [];
    public currentDeviceId: string | undefined;
    public displayMessages: string[] = [];
    public minHeight = 0;
    public recognised: { [key: string]: RecognisedBarcode } = {};
    public refocusIcon = faRedo;
    public patternIcon = faFilter;
    public showPattern = false;

    private stream: MediaStream | undefined;
    private refocusTrigger = new Subject<void>();
    private destroyed$: Subject<void> = new Subject<void>();

    public constructor() {
        effect(() => {
            const pattern = this.pattern();
            for (const scanResult of Object.values(this.recognised)) {
                scanResult.matchesPattern = matchesPattern(scanResult.readResult.text, pattern);
                scanResult.displayString = this.scanUtilities.getBarcodeDisplayString(
                    scanResult.readResult.text,
                    this.appId(),
                    pattern,
                );
            }
        });
    }

    public ngOnInit(): void {
        this.initCams();
        this.run();
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.stopStream(this.stream);
    }

    public refocus(): void {
        this.refocusTrigger.next();
    }

    public selectBarcode(result: RecognisedBarcode): void {
        this.barcodeSelected.emit(result.readResult.text);
    }

    private run(): void {
        this.refocusTrigger
            .asObservable()
            .pipe(
                debounceTime(BARCODE_REFOCUS_DEBOUNCE),
                switchMap(() => this.restartStream()),
                takeUntil(this.destroyed$),
            )
            .subscribe();

        // Perform operations outside Angular's zone to prevent triggering change detection and, consequently, re-rendering
        this.zone.runOutsideAngular(() => {
            const trigger = new Subject<void>();
            trigger
                .asObservable()
                .pipe(
                    delay(BARCODE_LOOP_DELAY),
                    map(() => this.takeImage()),
                    switchMap((image) => from(this.detectBarcode(image))),
                    takeUntil(this.destroyed$),
                )
                .subscribe((results) => {
                    results.forEach((result) => {
                        const key = `${result.format}::${result.text}`;
                        if (!this.recognised[key]) {
                            this.recognised[key] = {
                                timestamp: Date.now(),
                                readResult: result,
                                matchesPattern: matchesPattern(result.text, this.pattern()),
                                displayString: this.scanUtilities.getBarcodeDisplayString(
                                    result.text,
                                    this.appId(),
                                    this.pattern(),
                                ),
                            };
                            this.cdr.markForCheck();
                            this.barcodeSuccessfullyScanned.emit();
                        }
                    });
                    trigger.next();
                });
            trigger.next();
        });
    }

    private restartStream(): Observable<unknown> {
        const videoElement = this.video;
        if (videoElement && !this.minHeight) {
            this.minHeight = videoElement.offsetHeight;
        }
        const deviceId = this.currentDeviceId;
        this.startStream(undefined);
        return timer(BARCODE_STREAM_RESTART_DELAY).pipe(
            tap(() => {
                if (!this.currentDeviceId) {
                    this.startStream(deviceId);
                }
            }),
            delay(BARCODE_STREAM_RESTART_COOLDOWN),
            tap(() => {
                this.minHeight = 0;
            }),
        );
    }

    private initCams(): void {
        if (!navigator?.mediaDevices?.getUserMedia) {
            this.displayMessages.push('No video device support available.');
            this.log.error(this.constructor.name, 'No video device support available.');
            return;
        }

        navigator.mediaDevices
            .getUserMedia({ video: true })
            .catch((e) => {
                this.displayMessages.push('Accessing video device failed.');
                this.log.error(this.constructor.name, 'Accessing video device failed.', e);
                throw e;
            })
            .then((stream) => this.stopStream(stream))
            .then(() => navigator.mediaDevices.enumerateDevices())
            .then((devices) => {
                this.log.debug(
                    `Found ${devices.length} devices:`,
                    devices.map((d) => `${d.deviceId} -> ${d.label} (${d.kind})`).join(', '),
                );
                this.devices = devices.filter((device) => device.kind === 'videoinput');
                if (this.devices.length > 0) {
                    const lastCam = localStorage.getItem(this.LOCAL_STORAGE_LAST_CAM);
                    this.startStream(
                        this.devices.find((device) => device.deviceId === lastCam)?.deviceId ||
                            this.devices[0].deviceId,
                    );
                } else {
                    this.displayMessages.push('No video device available.');
                    this.log.error(this.constructor.name, 'No video device available.');
                }
            })
            .catch((e) => this.log.error(`${this.constructor.name}: Error initialising cameras: ${JSON.stringify(e)}`));
    }

    private startStream(deviceId: string | undefined): void {
        this.stopStream(this.stream);
        this.currentDeviceId = deviceId;
        const videoElement = this.video;
        if (!this.currentDeviceId?.length) {
            if (videoElement) {
                videoElement.srcObject = null;
            }
            return;
        }

        localStorage.setItem(this.LOCAL_STORAGE_LAST_CAM, this.currentDeviceId);

        navigator.mediaDevices
            .getUserMedia({
                video: { deviceId: { exact: deviceId } },
            })
            .then((stream) => {
                this.stream = stream;
                if (videoElement) {
                    videoElement.srcObject = stream;
                }
            })
            .catch((error) => {
                this.log.error('Error accessing webcam: ', error);
                this.displayMessages.push('Error accessing webcam.');
            });
    }

    public switchCamera(deviceId: string): void {
        this.startStream(deviceId);
    }

    private getVideoAspectRatio(): number {
        const videoElement = this.video;
        if (
            videoElement?.videoWidth &&
            videoElement.videoWidth > 0 &&
            videoElement.videoHeight &&
            videoElement.videoHeight > 0
        ) {
            return videoElement.videoWidth / videoElement.videoHeight;
        }

        return 1;
    }

    private takeImage(): string | undefined {
        const canvas = document.createElement('canvas');
        const video = this.video;
        if (!video || !canvas) {
            return undefined;
        }

        canvas.height = this.rawHeight();
        canvas.width = canvas.height * this.getVideoAspectRatio();

        const context = canvas.getContext('2d');
        if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL('image/png');
        }
        return undefined;
    }

    private async detectBarcode(image: string | undefined): Promise<ReadResult[]> {
        if (!image) {
            return [];
        }
        return fetch(image)
            .then((res) => res.blob())
            .then((blob) => readBarcodes(blob, this.readerOptions()));
    }

    private stopStream(stream: MediaStream | undefined): void {
        stream?.getTracks().forEach((track) => track.stop());
    }

    private get video(): HTMLVideoElement | undefined {
        return this.videoElement?.nativeElement as HTMLVideoElement;
    }

    public toggleShowPattern(): void {
        this.showPattern = !this.showPattern;
    }
}
